import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { MAX_ESTIMATED_MINUTES } from '../helpers/minutes.js'
import { irrigationProblemSubjectSchema } from '../enums/irrigation.enum.js'
import { repairMaterialsSchema } from './irrigation.input.js'

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullish()

/**
 * The writable fields of a problem type, with no defaults, so the update
 * schema built from them leaves an absent key alone (the `.partial()` trap in
 * docs/field-survey.md).
 */
const writableFields = {
  name: z.string().trim().min(1, 'A problem needs a name').max(80),
  /** Written for a client: it reaches a proposal. */
  description: optionalText(2000),
  appliesTo: z
    .array(irrigationProblemSubjectSchema)
    .min(1, 'Say what this problem can be found on')
    .refine(
      (subjects) => new Set(subjects).size === subjects.length,
      'Each is listed once.',
    ),
  defaultLaborMinutes: z
    .number()
    .int()
    .nonnegative()
    .max(MAX_ESTIMATED_MINUTES)
    .nullable(),
  /** Sent whole: the list replaces the type's usual materials. */
  defaultMaterials: repairMaterialsSchema,
  /** An already-uploaded library photo to use as the stock photograph. */
  photoDocumentId: uuidSchema.nullable(),
  /** An already-reserved stock clip to use as the explainer. */
  videoId: uuidSchema.nullable(),
}

export const createIrrigationProblemTypeInputSchema = z
  .object({
    ...writableFields,
    defaultLaborMinutes: writableFields.defaultLaborMinutes.default(null),
    defaultMaterials: writableFields.defaultMaterials.default([]),
    photoDocumentId: writableFields.photoDocumentId.default(null),
    videoId: writableFields.videoId.default(null),
  })
  .strict()

export type CreateIrrigationProblemTypeInput = z.infer<
  typeof createIrrigationProblemTypeInputSchema
>

export const updateIrrigationProblemTypeInputSchema = z
  .object(writableFields)
  .partial()
  .strict()

export type UpdateIrrigationProblemTypeInput = z.infer<
  typeof updateIrrigationProblemTypeInputSchema
>
